import React, {useEffect, useMemo, useState} from "react";
import {ProductCategoryJson, SavedReport} from "../types";
import {DashboardCard} from "@/ReactInit/Scripts/DashboardPage/components/DashboardCard";
import {getReportYears} from "@/ReactInit/Scripts/DashboardPage/utils/reportYears";
import {
  buildCategoryByProductMap,
  getCategoryForProductName,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import {
  formatDate,
  formatVolumeKg,
  parseDashboardDate,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";

interface ReportHistoryViewProps {
  currentYearReport: SavedReport | null;
  previousYearReport: SavedReport | null;
}

const {currentYear, previousYear} = getReportYears();

const ReportMetaLine = ({year, report}: { year: number; report: SavedReport }) => {
  const submittedDt = parseDashboardDate(report.reportDate);
  const changedDt = parseDashboardDate(report.changedDate);

  const submitted = submittedDt ? formatDate(submittedDt) : null;

  const changed =
    changedDt &&
    (!submittedDt || changedDt.getTime() !== submittedDt.getTime())
      ? formatDate(changedDt)
      : null;

  return (
    <div className="flex flex-col gap-1 tablet:flex-row tablet:items-end tablet:gap-3 mb-5 tablet:mb-3">
      <h3 className="text-2xl font-semibold leading-7 tablet:text-3xl">
        {year}-rapport
      </h3>
      <div className="flex flex-col tablet:flex-row items-start tablet:items-center gap-x-3 leading-5">
        {submitted &&
            <span className="text-sm px-3 py-1 bg-[rgba(0,0,0,0.03)] rounded border border-[rgba(0,0,0,0.1)]"><strong>Innlevert:</strong> {submitted}</span>}
        {changed &&
            <span className="text-sm px-3 py-1 bg-[rgba(0,0,0,0.03)] rounded border border-[rgba(0,0,0,0.1)]"><strong>Sist redigert:</strong> {changed}</span>}
      </div>
    </div>
  );
};

const ReportHistoryView: React.FC<ReportHistoryViewProps> = (
  {
    currentYearReport,
    previousYearReport,
  }) => {
  const [categories, setCategories] = useState<ProductCategoryJson[]>([]);

  useEffect(() => {
    fetch("/dashboard/json/productCategories.json")
      .then((res) => res.json())
      .then((data: ProductCategoryJson[]) => setCategories(data))
      .catch(console.error);
  }, []);

  const categoryByProduct = useMemo(
    () => buildCategoryByProductMap(categories),
    [categories],
  );

  const getCategoryLabel = (productName: string): string => {
    if (!productName?.trim()) return "—";
    const category = getCategoryForProductName(productName, categoryByProduct);
    return category === "Ukjent kategori" ? "—" : category;
  };

  const renderReportSection = (report: SavedReport | null, year: number) => {
    const numbers = report?.numbers ?? [];
    const sortedNumbers = [...numbers].sort((a, b) => a.productName.localeCompare(b.productName, "nb"),);
    if (!report || numbers.length === 0) {
      return (
        <div>
          <h3 className="mb-3 text-2xl font-semibold leading-7 tablet:text-3xl">
            {year}-rapport
          </h3>
          <p className="text-base text-black-subtile">
            Ingen rapport registrert for {year}.
          </p>
        </div>
      );
    }
    return (
      <div>
        <ReportMetaLine year={year} report={report}/>
        <div>
          <div className="overflow-x-auto">
            <table className="dashboardTable reportTable">
              <thead>
              <tr>
                <th>Produkt</th>
                <th>Produktkategori</th>
                <th>{`Volum i ${year}`}</th>
              </tr>
              </thead>
              <tbody>
              {sortedNumbers.map((n) => (
                <tr key={n.numberId}>
                  <td>{n.productName}</td>
                  <td>{getCategoryLabel(n.productName)}</td>
                  <td>{formatVolumeKg(n.volume, {empty: "—"})}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardCard as="section" className="flex flex-col gap-5 tablet:gap-10">
      <h2 className="text-4xl font-semibold leading-tight tablet:font-5xl">
        Rapporthistorikk
      </h2>

      <div className="flex flex-col gap-8">
        {/* Current year's report */}
        {renderReportSection(currentYearReport, currentYear)}

        {/* Last year's report */}
        {renderReportSection(previousYearReport, previousYear)}
      </div>
    </DashboardCard>
  );
};

export default ReportHistoryView;
